import {
	Injectable,
	Injector,
	Signal,
	computed,
	effect,
	inject,
	runInInjectionContext,
	signal,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
	Form,
	FormField,
	FormSubSection,
	FormValue,
} from '@models/client-intake';
import {
	isFieldViewModel,
	isFormField,
	isFormSubSection,
	isSubSectionViewModel,
} from '@globalUtils/client-intake';
import { evaluateConditionalRule } from '../../../utils/conditional-rule-evaluator';
import {
	FieldViewModel,
	FormViewModel,
	SectionViewModel,
	SubSectionViewModel,
} from '@domain/intake-form-client-portal';

/**
 * This is a straight port from Client Portal for the demo - Squad 2 will refactor and clean
 * up during intake x engine rework
 */
@Injectable({
	providedIn: 'root',
})
export class IntakeFormVmService {
	private formBuilder: FormBuilder = inject(FormBuilder);

	createFormViewModel(form: Form, injector: Injector): FormViewModel {
		const formGroup = this.formBuilder.group({});
		const formValues_ = runInInjectionContext(injector, () =>
			toSignal(
				formGroup.valueChanges.pipe(debounceTime(100), distinctUntilChanged()),
				{ initialValue: formGroup.value },
			),
		);

		const sections = form.sections.map((section) => ({
			section,
			items: this.createItemViewModels(section.fields, formValues_),
		}));
		this.updateFormControls(sections, formGroup);

		runInInjectionContext(injector, () =>
			effect(() => {
				void formValues_();
				this.updateFormControls(sections, formGroup);
			}),
		);

		// Use dummy value for form preview
		const intakeFormReadOnly = signal({
			isReadOnly: false,
			reason: {
				inviteExpired: false,
				intakeFormCompleted: false,
			},
		});

		return {
			form,
			formGroup,
			sections,
			formValues_,
			intakeFormReadOnly,
			cleanupFn: (): void => {
				/* no-op */
			},
		};
	}

	private createItemViewModels(
		items: Array<FormField | FormSubSection>,
		formValues_: Signal<Record<string, FormValue>>,
	): Array<FieldViewModel | SubSectionViewModel> {
		return items.map((item) => {
			if (isFormField(item)) {
				const control = this.formBuilder.control(item.defaultValue ?? '');
				return {
					field: item,
					control,
					isVisible_: computed(() =>
						item.conditionalRule
							? evaluateConditionalRule(
									item.conditionalRule,
									(questionId) =>
										(formValues_()[questionId] as FormValue) ?? null,
								)
							: true,
					),
				};
			} else if (isFormSubSection(item)) {
				const { fields, ...subSectionWithoutFields } = item;
				return {
					subSection: subSectionWithoutFields,
					isVisible_: computed(() =>
						item.conditionalRule
							? evaluateConditionalRule(
									item.conditionalRule,
									(questionId) =>
										(formValues_()[questionId] as FormValue) ?? null,
								)
							: true,
					),
					fields: this.createItemViewModels(fields, formValues_),
				};
			} else {
				throw new Error(`Unknown item type: ${JSON.stringify(item)}`);
			}
		});
	}

	private updateFormControls(
		sections: SectionViewModel[],
		formGroup: FormGroup,
	): void {
		sections.forEach((section) =>
			this.updateItemControls(section.items, formGroup),
		);
	}

	private updateItemControls(
		items: Array<FieldViewModel | SubSectionViewModel>,
		formGroup: FormGroup,
	): void {
		items.forEach((item) => {
			if (isFieldViewModel(item)) {
				const visible = item.isVisible_();
				const fieldId = item.field.id;
				if (!formGroup.contains(fieldId)) {
					formGroup.addControl(fieldId, item.control, { emitEvent: false });
				}
				if (visible) {
					try {
						const isFileInput = item.field.type === 'file';
						if (item.control.disabled)
							item.control.enable({ emitEvent: !isFileInput });
					} catch (e) {
						console.error('Error enabling control', e);
					}
				} else {
					try {
						const isFileInput = item.field.type === 'file';
						if (item.control.enabled)
							item.control.disable({ emitEvent: !isFileInput });
						item.control.setValidators([]);
					} catch (e) {
						console.error('Error disabling control', e);
					}
				}
				item.control.updateValueAndValidity({ emitEvent: false });
			} else if (isSubSectionViewModel(item)) {
				if (item.isVisible_()) this.updateItemControls(item.fields, formGroup);
				else this.disableItemControls(item.fields, formGroup);
			} else {
				throw new Error(`Unknown view model type: ${JSON.stringify(item)}`);
			}
		});
	}

	private disableItemControls(
		items: Array<FieldViewModel | SubSectionViewModel>,
		formGroup: FormGroup,
	): void {
		items.forEach((item) => {
			if (isFieldViewModel(item)) {
				const fieldId = item.field.id;
				if (!formGroup.contains(fieldId)) {
					formGroup.addControl(fieldId, item.control, { emitEvent: false });
				}
				const isFileInput = item.field.type === 'file';
				if (item.control.enabled)
					item.control.disable({ emitEvent: !isFileInput });
				item.control.setValidators([]);
				item.control.updateValueAndValidity({ emitEvent: false });
			} else if (isSubSectionViewModel(item)) {
				this.disableItemControls(item.fields, formGroup);
			}
		});
	}
}
